/**
 * OpeningstijdenEditor — weekschema (blokken per dag) + uitzonderingsdagen.
 *
 * Puur presentational + controlled: ontvangt schedule/overrides en meldt
 * wijzigingen omhoog; TelefoonInstellingenPage doet de PUT naar de settings-API.
 */

import clsx from 'clsx';

export interface ScheduleBlock {
  from: string;
  to: string;
}

export type WeekSchedule = Record<string, ScheduleBlock[]>;

export interface OverrideAction {
  type: 'ring' | 'forward' | 'voicemail' | 'announce';
  target?: string | null;
  greeting_id?: string | null;
}

export interface OverrideRow {
  date: string;
  label: string | null;
  closed: boolean;
  blocks: ScheduleBlock[] | null;
  /** Afwijkende actie voor deze dag (bv. voicemail met feestdag-begroeting). */
  action_override?: OverrideAction | null;
}

const DAYS: Array<[string, string]> = [
  ['mon', 'Maandag'],
  ['tue', 'Dinsdag'],
  ['wed', 'Woensdag'],
  ['thu', 'Donderdag'],
  ['fri', 'Vrijdag'],
  ['sat', 'Zaterdag'],
  ['sun', 'Zondag'],
];

export interface GreetingChoice {
  id: string;
  name: string;
  kind: string;
  active: boolean;
}

interface Props {
  schedule: WeekSchedule;
  overrides: OverrideRow[];
  onScheduleChange: (schedule: WeekSchedule) => void;
  onOverridesChange: (overrides: OverrideRow[]) => void;
  /** Beschikbare begroetingen voor de per-dag-keuze (kind closed/voicemail). */
  greetings?: GreetingChoice[];
  /** "Schrijf begroeting voor deze dag" — opent de generator met prefill. */
  onWriteGreeting?: (overrideIndex: number) => void;
}

export function OpeningstijdenEditor({
  schedule,
  overrides,
  onScheduleChange,
  onOverridesChange,
  greetings,
  onWriteGreeting,
}: Props) {
  const updateBlock = (day: string, index: number, field: 'from' | 'to', value: string) => {
    const blocks = (schedule[day] ?? []).map((b, i) =>
      i === index ? { from: field === 'from' ? value : b.from, to: field === 'to' ? value : b.to } : b,
    );
    onScheduleChange({ ...schedule, [day]: blocks });
  };

  const addBlock = (day: string) => {
    const blocks = [...(schedule[day] ?? []), { from: '09:00', to: '17:00' }];
    onScheduleChange({ ...schedule, [day]: blocks });
  };

  const removeBlock = (day: string, index: number) => {
    const blocks = (schedule[day] ?? []).filter((_, i) => i !== index);
    onScheduleChange({ ...schedule, [day]: blocks });
  };

  const addOverride = () => {
    const today = new Date().toISOString().slice(0, 10);
    onOverridesChange([...overrides, { date: today, label: '', closed: true, blocks: null }]);
  };

  const updateOverride = (index: number, patch: Partial<OverrideRow>) => {
    const next = overrides.map((o, i) => (i === index ? { ...o, ...patch } : o));
    onOverridesChange(next);
  };

  const removeOverride = (index: number) => {
    onOverridesChange(overrides.filter((_, i) => i !== index));
  };

  const overrideGreetings = (greetings ?? []).filter((g) => g.kind === 'closed' || g.kind === 'voicemail');

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        {DAYS.map(([key, label]) => {
          const blocks = schedule[key] ?? [];
          return (
            <div key={key} className="flex items-start gap-3">
              <span className={clsx('w-24 pt-1.5 text-sm', blocks.length === 0 ? 'text-slate-400' : 'text-slate-700')}>
                {label}
              </span>
              <div className="flex-1 space-y-1.5">
                {blocks.length === 0 && <span className="inline-block pt-1.5 text-xs text-slate-400">Gesloten</span>}
                {blocks.map((b, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="time"
                      value={b.from}
                      onChange={(e) => updateBlock(key, i, 'from', e.target.value)}
                      className="border border-slate-200 rounded px-2 py-1 text-sm"
                    />
                    <span className="text-slate-400 text-sm">tot</span>
                    <input
                      type="time"
                      value={b.to}
                      onChange={(e) => updateBlock(key, i, 'to', e.target.value)}
                      className="border border-slate-200 rounded px-2 py-1 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => removeBlock(key, i)}
                      aria-label={`Blok verwijderen op ${label}`}
                      className="text-slate-400 hover:text-red-600 text-sm px-1"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => addBlock(key)}
                className="pt-1.5 text-xs text-indigo-600 hover:text-indigo-800 whitespace-nowrap"
              >
                + blok
              </button>
            </div>
          );
        })}
      </div>

      <div className="pt-4 border-t border-slate-100">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-slate-800">Uitzonderingsdagen</h3>
          <button type="button" onClick={addOverride} className="text-xs text-indigo-600 hover:text-indigo-800">
            + uitzondering
          </button>
        </div>
        {overrides.length === 0 ? (
          <p className="text-xs text-slate-400">Geen uitzonderingen (feestdagen, incidentele sluiting).</p>
        ) : (
          <ul className="space-y-2">
            {overrides.map((o, i) => (
              <li key={i} className="flex flex-wrap items-center gap-2">
                <input
                  type="date"
                  value={o.date}
                  onChange={(e) => updateOverride(i, { date: e.target.value })}
                  className="border border-slate-200 rounded px-2 py-1 text-sm"
                />
                <input
                  type="text"
                  placeholder="Label (bijv. Koningsdag)"
                  value={o.label ?? ''}
                  onChange={(e) => updateOverride(i, { label: e.target.value })}
                  className="border border-slate-200 rounded px-2 py-1 text-sm flex-1 min-w-[10rem]"
                />
                <label className="flex items-center gap-1.5 text-xs text-slate-600">
                  <input
                    type="checkbox"
                    checked={o.closed}
                    onChange={(e) => updateOverride(i, { closed: e.target.checked, blocks: e.target.checked ? null : [{ from: '09:00', to: '13:00' }] })}
                  />
                  Gesloten
                </label>
                {!o.closed && (o.blocks ?? []).map((b, bi) => (
                  <span key={bi} className="flex items-center gap-1">
                    <input
                      type="time"
                      value={b.from}
                      onChange={(e) => {
                        const blocks = (o.blocks ?? []).map((blk, j) =>
                          j === bi ? { from: e.target.value, to: blk.to } : blk,
                        );
                        updateOverride(i, { blocks });
                      }}
                      className="border border-slate-200 rounded px-1.5 py-0.5 text-xs"
                    />
                    <span className="text-slate-400 text-xs">-</span>
                    <input
                      type="time"
                      value={b.to}
                      onChange={(e) => {
                        const blocks = (o.blocks ?? []).map((blk, j) =>
                          j === bi ? { from: blk.from, to: e.target.value } : blk,
                        );
                        updateOverride(i, { blocks });
                      }}
                      className="border border-slate-200 rounded px-1.5 py-0.5 text-xs"
                    />
                  </span>
                ))}
                {overrideGreetings.length > 0 && (
                  <select
                    value={o.action_override?.greeting_id ?? ''}
                    onChange={(e) =>
                      updateOverride(i, {
                        action_override: e.target.value
                          ? { type: 'voicemail', greeting_id: e.target.value }
                          : null,
                      })
                    }
                    title="Begroeting voor deze dag"
                    className="border border-slate-200 rounded px-1.5 py-0.5 text-xs"
                  >
                    <option value="">Standaard begroeting</option>
                    {overrideGreetings.map((g) => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                )}
                {onWriteGreeting && (
                  <button
                    type="button"
                    onClick={() => onWriteGreeting(i)}
                    className="text-xs text-indigo-600 hover:text-indigo-800 whitespace-nowrap"
                  >
                    Schrijf begroeting
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => removeOverride(i)}
                  aria-label="Uitzondering verwijderen"
                  className="text-slate-400 hover:text-red-600 text-sm px-1"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
